<template>
  <div class="bg-white rounded-2xl shadow-md p-6 space-y-4">
    <h2 class="text-lg font-bold">Configuration</h2>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
      <StackedField label="Potentiel de ventes">
        <FormField
          id="monthlySalesTarget"
          name="monthlySalesTarget"
          type="number"
          v-model="formData.monthlySalesTarget"
          required
        />
      </StackedField>
      <StackedField label="Catégorie">
        <Selector id="category" name="category" v-model="formData.category" :options="categoryOptions" />
      </StackedField>
      <StackedField v-if="isOffline" label="Région">
        <Selector id="region" name="region" v-model="formData.region" :options="getRegions()" />
      </StackedField>
      <StackedField v-if="isOffline" label="Enregistrer une vente">
        <Selector id="recordASale" name="recordASale" v-model="formData.recordASale" :options="booleanOptions" />
      </StackedField>
      <StackedField v-if="isOffline" label="Paiement des primes">
        <Selector
          id="retributionsPayoutMethod"
          name="retributionsPayoutMethod"
          v-model="formData.retributionsPayoutMethod"
          :options="retributionsPayoutMethodsOptions"
        />
      </StackedField>
      <StackedField v-if="isEstalyGiftCard" label="Fournisseur carte cadeau">
        <Selector
          id="giftCardProvider"
          name="giftCardProvider"
          v-model="formData.giftCardProvider"
          :options="giftCardProvidersOptions"
        />
      </StackedField>
      <StackedField v-if="isOffline" label="Booster">
        <Selector
          id="retributionsBoostEnabled"
          name="retributionsBoostEnabled"
          v-model="formData.retributionsBoostEnabled"
          :options="booleanOptions"
        />
      </StackedField>
    </div>
    <div class="flex justify-end gap-x-3">
      <SecondaryButton @click="$emit('previous')">Retour</SecondaryButton>
      <SecondaryButton @click="submit" :loading="saving">Valider la configuration</SecondaryButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { StackedField, FormField, Selector, SecondaryButton } from "@estaly/ui";

export default {
  name: "StoreOnboardingStepConfiguration",
  components: { StackedField, FormField, Selector, SecondaryButton },
  props: { store: { type: Object, required: true } },
  emits: ["activate-store", "previous"],
  data() {
    return {
      saving: false,
      formData: {
        monthlySalesTarget: this.store.monthlySalesTarget || "",
        category: this.store.category || "",
        region: this.store.region || "",
        recordASale: this.store.storeFeatureConfiguration?.recordASale ?? false,
        retributionsPayoutMethod: this.store.retributionsPayoutMethod || "",
        giftCardProvider: this.store.giftCardProvider || "",
        retributionsBoostEnabled: this.store.retributionsBoostEnabled ?? false,
      },
      booleanOptions: [
        { value: true, label: "Activé" },
        { value: false, label: "Désactivé" },
      ],
    };
  },
  computed: {
    ...mapGetters("stores", {
      getRegions: "getRegions",
      getCategories: "getCategories",
      getRetributionPayoutMethods: "getRetributionPayoutMethods",
      getGiftCardProviders: "getGiftCardProviders",
      isStoreOfflineGetter: "isStoreOffline",
    }),
    isOffline() {
      return this.isStoreOfflineGetter(this.store);
    },
    isEstalyGiftCard() {
      return this.isOffline && this.formData.retributionsPayoutMethod === "estaly_gift_card";
    },
    categoryOptions() {
      return this.getCategories().map((c) => ({ value: c, label: c }));
    },
    retributionsPayoutMethodsOptions() {
      return this.getRetributionPayoutMethods().map((m) => ({
        value: m,
        label: this.$t(`store.retributions_payout_method.${m}`),
      }));
    },
    giftCardProvidersOptions() {
      return this.getGiftCardProviders().map((p) => ({
        value: p,
        label: this.$t(`store.gift_card_provider.${p}`),
      }));
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("stores", ["update"]),
    async submit() {
      this.saving = true;
      const data = {
        monthlySalesTarget: this.formData.monthlySalesTarget,
        category: this.formData.category,
      };

      if (this.isOffline) {
        data.region = this.formData.region;
        data.recordASale = this.formData.recordASale;
        data.retributionsPayoutMethod = this.formData.retributionsPayoutMethod;
        data.giftCardProvider = this.isEstalyGiftCard ? this.formData.giftCardProvider : null;
        data.retributionsBoostEnabled = this.formData.retributionsBoostEnabled;
      }

      const response = await this.update({ storeId: this.store.id, data });
      this.saving = false;
      if (!response?.success) return;

      await this.notify({ category: "simple", type: "success", title: "Configuration enregistrée" });
      this.$emit("activate-store");
    },
  },
};
</script>
